import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * PayPal recurring subscription button (Pro / Business tiers).
 * createSubscription → backend creates a live PayPal subscription with the user's id.
 * onApprove → backend verifies with PayPal and unlocks the plan instantly.
 */
export default function PayPalSubscribeButton({ tier_id, price, onSuccess }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/paypal/config");
        setConfig(data);
      } catch (err) {
        setConfig({ configured: false });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return <div className="h-12 animate-pulse rounded-md bg-secondary/40" data-testid="paypal-sub-loading" />;
  }
  if (!config?.configured || !config?.client_id) {
    return (
      <div className="rounded-md border border-dashed border-amber-500/40 bg-amber-500/10 p-3 text-[11px] text-amber-400" data-testid="paypal-sub-not-configured">
        PayPal not configured yet — subscriptions unavailable.
      </div>
    );
  }

  const opts = {
    clientId: config.client_id,
    currency: "GBP",
    intent: "subscription",
    vault: true,
    components: "buttons",
    "data-sdk-integration-source": "zuntria",
  };

  return (
    <div data-testid={`paypal-subscribe-${tier_id}`}>
      <PayPalScriptProvider key="paypal-subscription" options={opts}>
        <PayPalButtons
          style={{ layout: "horizontal", shape: "rect", color: "blue", label: "subscribe", height: 45 }}
          createSubscription={async () => {
            try {
              const { data } = await api.post("/paypal/create-subscription", { tier_id });
              return data.subscription_id;
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Could not start subscription");
              throw err;
            }
          }}
          onApprove={async (data) => {
            try {
              const res = await api.post(`/paypal/subscription/activate/${data.subscriptionID}`);
              if (res.data?.ok) {
                toast.success("Subscription active!", {
                  description: `${price} — renews ${res.data.renews ? new Date(res.data.renews).toLocaleDateString() : "automatically"}. Cancel anytime.`,
                  duration: 10000,
                });
                onSuccess?.(res.data);
              } else {
                toast.info("Subscription pending — PayPal will confirm shortly. Your plan unlocks automatically.");
              }
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Activation failed — contact support if you were charged");
            }
          }}
          onError={(err) => {
            console.error("paypal subscribe error", err);
            toast.error("PayPal had an issue. Try again in a moment.");
          }}
          onCancel={() => toast.info("Subscription cancelled")}
        />
      </PayPalScriptProvider>
    </div>
  );
}
